import { filter, findIndex, isString, map } from 'lodash'
import { create } from 'zustand'
import {
    deleteAssistantSubscriptionById,
    getAssistantDetail,
    getAssistantFiltersList,
    getAssistantList,
    getAssistantSubscriptions,
    getIsFreeAssistant,
    getModelDetail,
    getModelList,
    patchAssistantById,
    patchAssistantSubscriptionById,
    postAssistantSubscriptionById,
} from '../api/assistant'
import { getStorage } from '../api/files'
import { AssistantShortDTO } from '../api/gen/base'
import { createProject, getProject, getProjectDetail } from '../api/project'
import { createPresignedUrl, uploadFile } from '../api/record'
import { changeTheadAssistant, getReplyCost } from '../api/request'
import { MODEL_ALIAS_LIST } from '../constants/constants'
import {
    AssistantFiltersOutputDTO,
    LLMModelDTO,
    LLMModelListOutputDTO,
    LlmModelListApiV1AiLlmModelGetParams,
    WorkplaceCreateModel,
    WorkplaceDTO,
    WorkplaceDetailApiV1AdjutantAppWorkplaceWorkplaceIdGetParams,
    WorkplaceListApiV1AdjutantAppWorkplaceGetParams,
    WorkplaceListOutputDTO,
} from '../swagger/dto'
import { FileLifeType, FileObjectStorageInfoOutputDTO } from '../swagger/dtoGateway'
import { SidebarType } from '../types/sidebars'
import { handleUploadFileToStorage } from '../utils/files'

interface Actions {
    setStateNotification: (params: {
        isOpen: boolean
        text?: string
        isError: boolean
        action?: string
    }) => void
    setOpenDialogAddBotToMeetNow: (isOpen: boolean) => void
    setOpenDialogAddBotToMeet: (isOpen: boolean) => void
    setOpenDialogTranscriptVideo: (isOpen: boolean) => void
    setNameOpenDialog: (name?: string) => void
    setIsUpdateCreatedRequest: (isUpdate: boolean) => void
    setCurrentAgentAndAssistantIdDialog: ({
        agent,
        assistantId,
    }: {
        agent?: string
        assistantId?: number
    }) => void
    setCurrentProjectId: (projectId: number) => void
    fetchModelList: ({
        params,
    }: LlmModelListApiV1AiLlmModelGetParams) => Promise<LLMModelListOutputDTO>
    fetchProjectList: ({
        params,
    }: WorkplaceListApiV1AdjutantAppWorkplaceGetParams) => Promise<WorkplaceListOutputDTO>
    fetchAssistantFiltersList: () => Promise<void>
    updateProjectDetail: (data: Partial<WorkplaceDTO>) => void
    fetchProjectDetail: ({
        params,
    }: WorkplaceDetailApiV1AdjutantAppWorkplaceWorkplaceIdGetParams) => Promise<WorkplaceDTO>
    createProject: ({ data }: WorkplaceCreateModel) => Promise<WorkplaceDTO>
    uploadFile: (file: File) => Promise<string | undefined>
    fetchModelDetail: (llmModelSlug: string, assistantId?: number) => Promise<LLMModelDTO>

    fetchStorage: () => Promise<FileObjectStorageInfoOutputDTO>
    setRefreshStorage: (isRefresh: boolean) => void

    resetModelDetail: () => void
    setOpenLoader: (isOpen: boolean) => void
    setScrollToRequestInput: (isScroll: boolean) => void
    setWebSearch: (isWebSearch: boolean) => void
    setIsTempUser: (isTemp: boolean) => void
    reset: () => void
    setOpenDiscountDialog: (isOpen: boolean) => void
    setOpenSidebar: (isOpen: boolean) => void
    setCurrentAssistantDataInDialog: (newAssistant: AssistantShortDTO | null) => void
    setIsOpenUpdateSubscriptionDialog: (isOpen: boolean) => void
    uploadFiles: (params: { files: File[] }, idEntity: string | number) => Promise<string[]>

    setOpenRegularRequestsDialogThreadId: (threadId: number | null) => void

    setOpenSidebarType: (type: `${SidebarType}` | null) => void
}

interface State {
    isOpenNotification: boolean
    isNotificationError: boolean
    notificationText?: string
    notificationAction?: string
    isOpenDialogAddBotToMeetNow: boolean
    isOpenDialogAddBotToMeet: boolean
    isOpenDialogTranscriptVideo: boolean
    nameOpenDialog?: string
    isUpdateCreatedRequest: boolean
    currentAgentNameInDialog?: string
    currentAssistantIdDialog?: number
    currentProjectId?: number
    modelListFull?: LLMModelDTO[]
    modelListActive?: LLMModelDTO[]
    isLoadingModelList: boolean
    errorModelList?: string
    isLoadingAssistantFiltersList: boolean
    errorAssistantFiltersList?: string
    projectListFull?: WorkplaceDTO[]
    projectListActive?: WorkplaceDTO[]
    projectListArchive?: WorkplaceDTO[]
    isLoadingProjectList: boolean
    errorProjectList?: string
    projectDetail?: Partial<WorkplaceDTO>
    errorFetchProjectDetail?: string
    isLoadingFetchProjectDetail: boolean
    errorCreateProject?: string
    isLoadingCreateProject: boolean
    assistantFiltersList: AssistantFiltersOutputDTO
    isDoneUploadFiles: boolean
    filesUrl?: string[]
    errorUploadFiles?: string
    isLoadingUploadFiles: boolean
    modelDetail?: LLMModelDTO
    isLoadingFetchModelDetail: boolean
    isOpenLoader: boolean
    errorFetchModelDetail?: string
    isNoneUserTariff: boolean
    isScrollToRequestInput: boolean
    isWebSearch: boolean
    isTempUser?: boolean
    storage?: FileObjectStorageInfoOutputDTO
    errorFetchStorage?: string
    isLoadingFetchStorage: boolean
    isRefreshStorage: boolean
    isOpenDiscountDialog: boolean
    isOpenSidebar: boolean
    hideHeader: boolean
    setHideHeader: (hideHeader: boolean) => void
    currentAssistantDataInDialog: AssistantShortDTO | null
    isOpenUpdateSubscriptionDialog: boolean
    openRegularRequestsDialogThreadId: number | null
    openSidebarType: `${SidebarType}` | null
}

const initialState: State = {
    isOpenNotification: false,
    isNotificationError: false,
    notificationText: undefined,
    notificationAction: undefined,
    isOpenDialogAddBotToMeetNow: false,
    isOpenDialogAddBotToMeet: false,
    isOpenDialogTranscriptVideo: false,
    nameOpenDialog: undefined,
    currentAgentNameInDialog: undefined,
    currentAssistantIdDialog: undefined,
    currentAssistantDataInDialog: null,
    isUpdateCreatedRequest: false,
    currentProjectId: undefined,
    modelListFull: undefined,
    modelListActive: undefined,
    isLoadingModelList: false,
    errorModelList: undefined,
    assistantFiltersList: [],
    isDoneUploadFiles: false,
    filesUrl: undefined,
    errorUploadFiles: undefined,
    isLoadingUploadFiles: false,
    projectDetail: undefined,
    errorFetchProjectDetail: undefined,
    isLoadingFetchProjectDetail: false,
    isLoadingCreateProject: false,
    errorCreateProject: undefined,
    projectListFull: undefined,
    projectListActive: undefined,
    projectListArchive: undefined,
    isLoadingProjectList: false,
    errorProjectList: undefined,
    errorFetchModelDetail: undefined,
    modelDetail: undefined,
    isOpenLoader: false,
    isLoadingFetchModelDetail: false,
    isNoneUserTariff: false,
    isScrollToRequestInput: false,
    isWebSearch: false,
    isTempUser: undefined,

    storage: undefined,
    errorFetchStorage: undefined,
    isLoadingFetchStorage: false,
    isRefreshStorage: false,

    isOpenDiscountDialog: false,
    isOpenSidebar: false,

    hideHeader: false,

    isOpenUpdateSubscriptionDialog: false,

    openRegularRequestsDialogThreadId: null,

    openSidebarType: null,
    isProtectedCurrentProject: false,
}

export const useWrapperStore = create<State & Actions>()((set, get) => ({
    ...initialState,
    requestByEdit: undefined,
    setRequestByEdit: (request) => {
        set({
            requestByEdit: request,
        })
    },
    dialogsOpen: false,
    setDialogsOpen: (dialogs) => {
        set({
            dialogsOpen: dialogs,
        })
    },
    stepOnboarding: 100,
    setStepOnboarding: (step) => {
        set({
            stepOnboarding: step,
        })
    },

    setStateNotification: ({ isOpen, text, isError, action = undefined }) => {
        set({
            notificationAction: action,
            isOpenNotification: isOpen,
            notificationText: text,
            isNotificationError: isError,
        })
    },

    setOpenDialogAddBotToMeetNow: (isOpen) => {
        set({
            isOpenDialogAddBotToMeetNow: isOpen,
        })
    },

    setOpenDialogAddBotToMeet: (isOpen) => {
        set({
            isOpenDialogAddBotToMeet: isOpen,
        })
    },

    setOpenDialogTranscriptVideo: (isOpen) => {
        set({
            isOpenDialogTranscriptVideo: isOpen,
        })
    },

    setNameOpenDialog: (name) => {
        set({
            nameOpenDialog: name,
        })
    },

    setIsUpdateCreatedRequest: (isUpdate) => {
        set({
            isUpdateCreatedRequest: isUpdate,
        })
    },

    setIsProtectedCurrentProject: (isProtected) => {
        set({
            isProtectedCurrentProject: isProtected,
        })
    },

    connectAssistantId: async (params, payload) => {
        try {
            set({
                isLoadingConnectAssistantId: true,
                errorConnectAssistantId: undefined,
            })

            const theadModified = await changeTheadAssistant(params, payload)

            if (theadModified.assistant_id === null) {
                theadModified.assistant_id = 'null'
            }

            set({
                isUpdateCreatedRequest: true,
                // modelDetail: undefined,
                currentAgentNameInDialog: theadModified.agent,
                currentAssistantIdDialog: theadModified.assistant_id,
                // theadDetail: theadModified,
                isLoadingConnectAssistantId: false,
            })
        } catch (error) {
            set({
                isLoadingConnectAssistantId: false,
                errorConnectAssistantId: 'Ошибка конекта ассистента',
            })
        }
    },

    setCurrentAgentAndAssistantIdDialog: ({ agent, assistantId }) => {
        set({
            currentAgentNameInDialog: agent,
            currentAssistantIdDialog: assistantId,
        })
    },

    setCurrentProjectId: (projectId) => {
        set({
            currentProjectId: projectId,
        })
    },

    fetchModelList: async (params) => {
        try {
            set({
                isNoneUserTariff: false,
                isLoadingModelList: true,
                errorModelList: undefined,
            })
            const data = await getModelList({ ...params, limit: 100 })
            const list = filter(data.objects, {
                is_restricted: false,
            })

            const modelListProtected = filter(data.objects, {
                is_protected: true,
            })

            const listByBestAndOptimal = []

            map(MODEL_ALIAS_LIST, (item, index) => {
                const isType = findIndex(list, { model_type: index })
                if (isType !== -1) {
                    listByBestAndOptimal.push(...item)
                }
            })

            const modelList = [...listByBestAndOptimal, ...list]

            set({
                isLoadingModelList: false,
                modelListFull: data.objects,
                modelListActive: modelList,
                modelListProtected: modelListProtected,
            })
        } catch (error) {
            if (error?.response?.data?.detail?.error_code === 'UserTariffDomainError') {
                set({
                    isNoneUserTariff: true,
                    isLoadingModelList: false,
                    errorModelList:
                        'Тариф не подключен, для продолжения работы выберите один из тарифов',
                })
            } else {
                set({
                    isLoadingModelList: false,
                    errorModelList: 'Ошибка получения списка моделей',
                })
            }
        }
    },

    fetchStorage: async () => {
        try {
            set({
                isLoadingFetchStorage: true,
                errorFetchStorage: undefined,
            })
            const data = await getStorage({ life_type: FileLifeType.PERMANENT })

            set({
                isRefreshStorage: false,
                isLoadingFetchStorage: false,
                storage: data,
            })
        } catch (error) {
            set({
                isRefreshStorage: false,
                isLoadingFetchStorage: false,
                errorFetchStorage: 'Ошибка получения данных хранилища',
            })
        }
    },

    fetchAssistantFiltersList: async () => {
        try {
            set({
                assistantFiltersList: [],
                isLoadingAssistantFiltersList: true,
                errorAssistantFiltersList: undefined,
            })
            const data = await getAssistantFiltersList()

            set({
                isLoadingAssistantFiltersList: false,
                assistantFiltersList: data,
            })
        } catch (error) {
            set({
                isLoadingAssistantFiltersList: false,
                errorAssistantFiltersList: 'Ошибка получения списка категорий ассистентов',
            })
        }
    },

    fetchAssistants: async () => {
        try {
            set({
                assistantsList: [],
            })
            const data = await getAssistantList({ limit: 1 })

            set({
                assistantsList: data,
            })
        } catch {}
    },
    updateAssistant: async (assistantId, data) => {
        try {
            set({
                isDoneUpdateAssistant: false,
                isLoadingUpdateAssistant: true,
                errorUpdateAssistant: undefined,
            })
            const response = await patchAssistantById(assistantId, data)

            set({
                isDoneUpdateAssistant: true,
                // assistantsList: [...assistantsListUpdate, ...response],
                isLoadingUpdateAssistant: false,
            })
        } catch (error) {
            set({
                isLoadingUpdateAssistant: false,
                isDoneUpdateAssistant: false,
                errorUpdateAssistant: 'Возникла ошибка',
            })
        }
    },

    setIsDoneUpdateAssistant: (isDoneUpdateAssistant) => {
        set({
            isDoneUpdateAssistant: isDoneUpdateAssistant,
        })
    },
    fetchAssistantDetail: async (assistantId, language) => {
        try {
            set({
                assistantsDetail: undefined,
            })
            const data = await getAssistantDetail({ assistantId, language })

            set({
                assistantsDetail: data,
            })
        } catch {}
    },

    createProject: async (data) => {
        try {
            set({
                isLoadingCreateProject: true,
                errorCreateProject: undefined,
            })
            const newProject = await createProject(data)
            const storedProjects = await get().projectListActive

            set({
                isLoadingCreateProject: false,
                projectListActive: [newProject, ...(storedProjects ?? [])],
            })
        } catch (error) {
            set({
                isLoadingCreateProject: false,
                errorCreateProject: 'Ошибка получения списка ассистетов',
            })
        }
    },

    fetchProjectList: async () => {
        try {
            set({
                isLoadingProjectList: true,
                errorProjectList: undefined,
            })
            const projectListFullData = await getProject({ limit: 100 })
            const projectListActiveData = filter(projectListFullData.objects, {
                is_archived: false,
            })
            const projectListArchiveData = filter(projectListFullData.objects, {
                is_archived: true,
            })

            set({
                isLoadingProjectList: false,
                projectListFull: projectListFullData.objects,
                projectListActive: projectListActiveData,
                projectListArchive: projectListArchiveData,
            })
        } catch (error) {
            set({
                isLoadingProjectList: false,
                errorProjectList: 'Ошибка получения списка проектов',
            })
        }
    },

    fetchProjectDetail: async (params) => {
        try {
            set({
                projectDetail: undefined,
                isLoadingFetchProjectDetail: true,
                errorFetchProjectDetail: undefined,
            })
            const data = await getProjectDetail(params)
            set({
                isLoadingFetchProjectDetail: false,
                projectDetail: data,
            })
        } catch (error) {
            set({
                isLoadingFetchProjectDetail: false,
                errorFetchProjectDetail: 'Ошибка получения деталей проекта',
            })
        }
    },

    setIsDoneUploadFiles: (isDone) => {
        set({
            isDoneUploadFiles: isDone,
        })
    },
    setOpenLoader: (isOpen) => {
        set({
            isOpenLoader: isOpen,
        })
    },

    setRefreshStorage: (isRefresh) => {
        set({
            isRefreshStorage: isRefresh,
        })
    },

    setWebSearch: (isWebSearch) => {
        set({
            isWebSearch,
        })
    },

    setIsTempUser: (isTemp) => {
        set({
            isTempUser: isTemp,
        })
    },

    uploadFiles: async (params, idEntity) => {
        let filesUrl: string[] = []

        try {
            set({
                isDoneUploadFiles: false,
                filesUrl: undefined,
                isLoadingUploadFiles: true,
                errorUploadFiles: undefined,
            })

            const uploadRequests: Promise<string>[] = []

            for (const file of params.files) {
                if (isString(file)) {
                    filesUrl.push(file)
                } else if (file.size === -1) {
                    filesUrl.push(file.name)
                } else {
                    uploadRequests.push(handleUploadFileToStorage(idEntity, file))
                }
            }

            filesUrl = [...filesUrl, ...(await Promise.all(uploadRequests))]

            set({
                isLoadingUploadFiles: false,
                filesUrl,
                isDoneUploadFiles: true,
            })
        } catch (error) {
            set({
                isLoadingUploadFiles: false,
                errorUploadFiles: 'Ошибка при добавлении, попробуйте еще раз',
            })
        }

        return filesUrl
    },
    uploadFile: async (file) => {
        try {
            set({
                isLoadingUploadFiles: true,
            })

            const name = file.name.replace(/[<>@#*+^,${}=|[\]\\]/g, '').replace(/\s+/g, '_')
            const { url, future_url, fields } = await createPresignedUrl({
                filename: name,
                life_type: FileLifeType.PERMANENT,
            })

            const formData = new FormData()

            Object.entries(fields).forEach(([key, value]) => {
                formData.append(key, value)
            })

            formData.append('file', file)

            await uploadFile({ url, formData })

            return future_url as string
        } catch (error) {
            set({
                isLoadingUploadFiles: false,
            })
        } finally {
            set({
                isLoadingUploadFiles: false,
            })
        }
    },
    updateProjectDetail: (data) => set({ projectDetail: { ...get().projectDetail, ...data } }),
    fetchModelDetail: async (llmModelSlug, assistantId) => {
        try {
            set({
                modelDetail: undefined,
                isLoadingFetchModelDetail: true,
                errorFetchModelDetail: undefined,
            })
            const data = await getModelDetail({ llmModelSlug })

            const dataAssistant = assistantId
                ? await getAssistantDetail({ assistantId })
                : undefined
            const dataModel = {
                ...data,
                series: {
                    ...data?.series,
                    additional_contexts: assistantId
                        ? [...dataAssistant?.params]
                        : [...data?.series?.additional_contexts],
                },
            }

            set({
                modelDetail: dataModel,
                isLoadingFetchModelDetail: false,
            })
        } catch (error) {
            set({
                isLoadingFetchModelDetail: false,
                errorFetchModelDetail: 'Ошибка получения деталей модели',
            })
        }
    },
    fetchReplyCost: async (params) => {
        try {
            set({
                isLoadingFetchReplyCost: true,
                errorFetchReplyCost: undefined,
            })
            const data = await getReplyCost(params)

            set({
                replyCost: data,
                isLoadingFetchReplyCost: false,
            })
        } catch (error) {
            set({
                isLoadingFetchReplyCost: false,
                errorFetchReplyCost: 'Ошибка получения стоимости запроса',
            })
        }
    },
    resetReplyCost: () => {
        set({
            replyCost: undefined,
        })
    },
    setScrollToRequestInput: (isScroll) => {
        set({
            isScrollToRequestInput: isScroll,
        })
    },
    resetModelDetail: () => {
        set({
            isOpenLoader: false,
            isOpenNotification: false,
            isNotificationError: false,
            notificationText: undefined,
            notificationAction: undefined,
            isUpdateCreatedRequest: false,
            modelDetail: undefined,
            requestByEdit: undefined,
            // currentAgentNameInDialog: undefined,
            // currentAssistantIdDialog: undefined,
        })
    },
    reset: () => {
        set(initialState)
    },

    setOpenDiscountDialog: (isOpen: boolean) => {
        set({
            isOpenDiscountDialog: isOpen,
        })
    },

    setOpenSidebar: (isOpen: boolean) => {
        set({
            isOpenSidebar: isOpen,
        })
    },
    setHideHeader: (hideHeader) => set({ hideHeader }),

    setCurrentAssistantDataInDialog: (newAssistant: AssistantShortDTO | null) => {
        set({
            currentAssistantDataInDialog: newAssistant,
        })
    },

    setIsOpenUpdateSubscriptionDialog: (isOpen) => {
        set({
            isOpenUpdateSubscriptionDialog: isOpen,
        })
    },

    setOpenRegularRequestsDialogThreadId: (threadId) => {
        set({
            openRegularRequestsDialogThreadId: threadId,
        })
    },

    setOpenSidebarType: (type) => {
        set({
            openSidebarType: type,
        })
    },

    fetchAssistantSubscriptions: async () => {
        try {
            set({
                // assistantSubscriptions: undefined,
                isLoadingFetchAssistantSubscriptions: true,
                errorFetchAssistantSubscriptions: undefined,
            })
            const data = await getAssistantSubscriptions()

            set({
                assistantSubscriptions: data.objects,
                isLoadingFetchAssistantSubscriptions: false,
            })
        } catch (error) {
            set({
                isLoadingFetchAssistantSubscriptions: false,
                errorFetchAssistantSubscriptions: 'Ошибка',
            })
        }
    },
    checkIsFreeAssistant: async (assistant_id) => {
        try {
            set({
                freeAssistant: undefined,
                isLoadingFetchFreeAssistant: true,
                errorFetchFreeAssistant: undefined,
            })
            const data = await getIsFreeAssistant(assistant_id)

            set({
                freeAssistant: data.is_available,
                isLoadingFetchAssistantSubscriptions: false,
            })
        } catch (error) {
            set({
                isLoadingFetchFreeAssistant: false,
                errorFetchFreeAssistant: 'Ошибка',
            })
        }
    },
    buyAssistantSubscription: async (assistant_id) => {
        try {
            set({
                isBuyAssistantSubscription: false,
                isLoadingFetchOperationAssistantSubscription: true,
                errorFetchOperationAssistantSubscription: undefined,
            })
            const data = await postAssistantSubscriptionById(assistant_id)

            set({
                isBuyAssistantSubscription: true,
                isLoadingFetchOperationAssistantSubscription: false,
            })
        } catch (error) {
            set({
                isLoadingFetchOperationAssistantSubscription: false,
                errorFetchOperationAssistantSubscription: 'Ошибка',
            })
        }
    },
    changeAssistantSubscription: async (userSubscriptionId) => {
        try {
            set({
                isLoadingFetchOperationAssistantSubscription: true,
                errorFetchOperationAssistantSubscription: undefined,
            })
            const data = await patchAssistantSubscriptionById(userSubscriptionId)

            set({
                isLoadingFetchOperationAssistantSubscription: false,
            })
        } catch (error) {
            set({
                isLoadingFetchOperationAssistantSubscription: false,
                errorFetchOperationAssistantSubscription: 'Ошибка',
            })
        }
    },

    removeAssistantSubscription: async (userSubscriptionId) => {
        try {
            set({
                isLoadingFetchOperationAssistantSubscription: true,
                errorFetchOperationAssistantSubscription: undefined,
            })
            const data = await deleteAssistantSubscriptionById(userSubscriptionId)

            set({
                isLoadingFetchOperationAssistantSubscription: false,
            })
        } catch (error) {
            set({
                isLoadingFetchOperationAssistantSubscription: false,
                errorFetchOperationAssistantSubscription: 'Ошибка',
            })
        }
    },
}))
